import * as yup from "yup";
import get from "lodash/get";

import {
  calculateColumnWidth,
  mapColumnTypes,
  TYPES,
} from "@/react/components/table/columns";
import selectColumnFactory from "@/react/components/table/components/selectColumnFactory";
import { getColumnDef } from "@/react/schema/schemaFields";

import featureFlagColumnMapper from "../common/featureFlagColumnMapper";
import {
  ConnectionMode,
  ExtensionType,
  OutboundPhoneMode,
  PhoneType,
  PrimaryLanguages,
  RecordingControls,
  SoundEffects,
} from "./types";
import schema from "./schema";
import agentActionsColumnFactory from "./actions/agentActionsColumnFactory";

export const COLUMN_DATA = [
  selectColumnFactory(),
  {
    ...getColumnDef(yup.reach(schema, "id"), "ID"),
    type: TYPES.ID,
    sticky: true,
  },
  getColumnDef(yup.reach(schema, "roles"), "Roles"),
  {
    ...getColumnDef(yup.reach(schema, "sso.sso_id"), "SSO ID"),
    type: TYPES.ID,
  },
  {
    ...getColumnDef(yup.reach(schema, "sso.sso_username"), "Username"),
    type: TYPES.ID,
  },
  /**
   * profile
   */
  {
    ...getColumnDef(yup.reach(schema, "profile.first_name"), "First Name"),
    editable: true,
  },
  {
    ...getColumnDef(yup.reach(schema, "profile.last_name"), "Last Name"),
    editable: true,
  },
  {
    ...getColumnDef(yup.reach(schema, "profile.display_name"), "Display Name"),
    editable: true,
  },
  {
    ...getColumnDef(yup.reach(schema, "profile.email"), "Email"),
    editable: true,
  },
  {
    ...getColumnDef(yup.reach(schema, "profile.comment"), "Comment"),
    editable: true,
  },
  /**
   * agent
   */
  getColumnDef(yup.reach(schema, "agent.legacy_guid"), "Legacy GUID"),
  getColumnDef(yup.reach(schema, "agent.legacy_uid"), "Legacy UID"),
  getColumnDef(yup.reach(schema, "agent.group_id"), "Group ID"),
  /**
   * agent.chat
   */
  {
    ...getColumnDef(
      yup.reach(schema, "agent.chat.concurrent_limit"),
      "Chat Concurrent Limit"
    ),
    type: TYPES.NUMBER,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.chat.agent_to_agent_enabled"),
      "Chat Agent-to-Agent"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  /**
   * agent.crm
   */
  {
    ...getColumnDef(
      yup.reach(schema, "agent.crm.hide_customer_contact_info_enabled"),
      "Hide Customer Contact Info"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  /**
   * agent.email
   */
  {
    ...getColumnDef(
      yup.reach(schema, "agent.email.pull_emails_from_queue_enabled"),
      "Pull Emails from Queue"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.email.delete_pending_emails_enabled"),
      "Delete Pending Emails"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.email.default_to_channel_from_address_enabled"),
      "Default To Channel From Address"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.email.default_from_address"),
      "Default From Address"
    ),
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.email.signature"),
      "Email Signature"
    ),
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.email.default_signature"),
      "Email Default Signature"
    ),
    editable: true,
  },
  /**
   * agent.smtp
   */
  {
    ...getColumnDef(
      yup.reach(schema, "agent.smtp.server_id"),
      "SMTP Server ID"
    ),
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.smtp.override_enabled"),
      "SMTP Override"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  /**
   * agent.interactions
   */
  {
    ...getColumnDef(
      yup.reach(schema, "agent.interactions.offer_timeout"),
      "Offer Timeout"
    ),
    type: TYPES.NUMBER,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.interactions.offer_rejection_enabled"),
      "Offer Rejection Enabled"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.interactions.primary_language"),
      "Interactions Primary Language"
    ),
    type: TYPES.SELECT,
    options: PrimaryLanguages,
    editable: true,
  },
  /**
   * agent.external_crm
   */
  {
    ...getColumnDef(
      yup.reach(schema, "agent.external_crm.username"),
      "External CRM Username"
    ),
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.external_crm.password"),
      "External CRM Password"
    ),
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.external_crm.salesforce_enabled"),
      "Salesforce"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  /**
   * agent.supervisor
   */
  {
    ...getColumnDef(
      yup.reach(schema, "agent.supervisor.enabled"),
      "Supervisor"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.supervisor.call_monitoring_enabled"),
      "Call Monitoring"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.supervisor.report_restriction_enabled"),
      "Report Restriction"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  /**
   * agent.phone
   */
  {
    ...getColumnDef(yup.reach(schema, "agent.phone.phone_type"), "Phone Type"),
    type: TYPES.SELECT,
    options: PhoneType,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.pstn_number"),
      "PSTN Number"
    ),
    type: TYPES.STRING,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.pstn_number_change_enabled"),
      "PSTN Change Enabled"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(yup.reach(schema, "agent.phone.ip_uri"), "Phone IP URI"),
    type: TYPES.STRING,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.ip_uri_change_enabled"),
      "Phone IP URI Change Enabled"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.phone_settings_validated"),
      "Phone Settings Validated"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.auto_answer_enabled"),
      "Auto Answer"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(yup.reach(schema, "agent.phone.pbx_number"), "PBX Number"),
    type: TYPES.NUMBER,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.connection_mode"),
      "Connection Mode"
    ),
    type: TYPES.SELECT,
    options: ConnectionMode,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.connection_mode_change_enabled"),
      "Connection Mode Change Enabled"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.extension_number"),
      "Extension Number"
    ),
    type: TYPES.NUMBER,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.extension_type"),
      "Extension Type"
    ),
    type: TYPES.SELECT,
    options: ExtensionType,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.dar_enabled"),
      "DAR Enabled"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.dar_voicemail_enabled"),
      "DAR Voicemail"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.calling_line_id"),
      "Calling Line ID"
    ),
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.outbound_phone_mode"),
      "Outbound Phone Mode"
    ),
    type: TYPES.SELECT,
    options: OutboundPhoneMode,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(
        schema,
        "agent.phone.recording_percentage_queued_inbound_calls"
      ),
      "Recording % Queued Inbound Calls"
    ),
    type: TYPES.NUMBER,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(
        schema,
        "agent.phone.recording_percentage_direct_inbound_calls"
      ),
      "Recording % Direct Inbound Calls"
    ),
    type: TYPES.NUMBER,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.recording_percentage_outbound_calls"),
      "Recording % Outbound Calls"
    ),
    type: TYPES.NUMBER,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.phone.recording_controls"),
      "Recording Controls"
    ),
    type: TYPES.SELECT,
    options: RecordingControls,
    editable: true,
  },
  /**
   * agent.ui
   */
  {
    ...getColumnDef(
      yup.reach(schema, "agent.ui.modify_queue_assignments_enabled"),
      "Modify Queue Assignments Enabled"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.ui.options_menu_enabled"),
      "Options Menu Enabled"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.ui.click_to_dial_enabled"),
      "Click-to-Dial Enabled"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.ui.notification_sound"),
      "Notification Sound"
    ),
    type: TYPES.SELECT,
    options: SoundEffects,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.ui.chat_message_browser_notification_enabled"),
      "Chat Message Browser Notification Enabled"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.ui.chat_message_sound"),
      "Chat Message Sound"
    ),
    type: TYPES.SELECT,
    options: SoundEffects,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.ui.status_code_list_enabled"),
      "Status Code List Enabled"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  {
    ...getColumnDef(
      yup.reach(schema, "agent.ui.display_customer_cli_enabled"),
      "Display Customer CLI Enabled"
    ),
    type: TYPES.BOOLEAN,
    editable: true,
  },
  agentActionsColumnFactory(),
];

const computeColumnIdToEditable = (tenantProfileFlags) =>
  new Map([
    [
      "profile.first_name",
      get(tenantProfileFlags, "allow_agent_change_name", true),
    ],
    [
      "profile.last_name",
      get(tenantProfileFlags, "allow_agent_change_name", true),
    ],
    [
      "profile.display_name",
      get(tenantProfileFlags, "allow_agent_change_display_name", true),
    ],
    // @TODO: Figure out what the other flags are intended to control.
    //    - allow_agent_change_connection_mode
    //    - show_allow_agent_reject_interactions_reset
    //    - show_allow_agent_change_connection_mode_reset
    //    - allow_agent_change_screen_pop
    //    - allow_agent_reject_interactions
    //    - agent_my_recording_enabled
    //    - allow_agent_configure_warning_popup
    //    - allow_agent_pull_email
  ]);

export default (tenantProfileFlags) => {
  let columns = COLUMN_DATA.map(mapColumnTypes);
  columns = calculateColumnWidth(columns);

  // Set some columns as non-editable depending on the flags set on
  // the tenant profile.
  const columnIdToEditable = computeColumnIdToEditable(tenantProfileFlags);
  const mapper = featureFlagColumnMapper(columnIdToEditable);
  columns = columns.map(mapper);

  return columns;
};
